import React, { useState } from 'react';
import { Shield, Clock, CheckCircle, XCircle, Play, Search, ChevronRight, AlertTriangle } from 'lucide-react';
import { ScanResultsModal } from '../components/ScanResultsModal';
export interface Scan {
  id: string;
  name: string;
  clientName: string;
  target: string;
  status: string;
  started: string;
  duration: string;
  findings: {
    high: number;
    medium: number;
    low: number;
  };
}
const scans: Scan[] = [{
  id: 'SCAN-2026-001',
  name: 'Full Environment Scan — Acme Corp',
  clientName: 'Acme Corp',
  target: 'AWS Production (3 accounts)',
  status: 'Running',
  started: '4 mins ago',
  duration: '—',
  findings: {
    high: 0,
    medium: 0,
    low: 0
  }
}, {
  id: 'SCAN-2026-002',
  name: 'Full Environment Scan — GlobalSecure LLC',
  clientName: 'GlobalSecure LLC',
  target: 'AWS + Azure (5 accounts)',
  status: 'Completed',
  started: '1 hour ago',
  duration: '2h 14m',
  findings: {
    high: 47,
    medium: 118,
    low: 278
  }
}, {
  id: 'SCAN-2026-003',
  name: 'Full Environment Scan — CloudNine Solutions',
  clientName: 'CloudNine Solutions',
  target: 'AWS + Azure + GCP (4 accounts)',
  status: 'Completed',
  started: '3 hours ago',
  duration: '1h 42m',
  findings: {
    high: 22,
    medium: 58,
    low: 76
  }
}, {
  id: 'SCAN-2026-004',
  name: 'Full Environment Scan — TechStart Inc',
  clientName: 'TechStart Inc',
  target: 'Azure (1 account)',
  status: 'Completed',
  started: '5 hours ago',
  duration: '38m 20s',
  findings: {
    high: 12,
    medium: 35,
    low: 40
  }
}, {
  id: 'SCAN-2026-005',
  name: 'Full Environment Scan — NetGuard Pro',
  clientName: 'NetGuard Pro',
  target: 'AWS + Azure (2 accounts)',
  status: 'Completed',
  started: 'Yesterday, 2:30 PM',
  duration: '52m 10s',
  findings: {
    high: 8,
    medium: 25,
    low: 30
  }
}, {
  id: 'SCAN-2026-006',
  name: 'Full Environment Scan — DataFlow Systems',
  clientName: 'DataFlow Systems',
  target: 'GCP (1 account)',
  status: 'Completed',
  started: 'Yesterday, 11:00 AM',
  duration: '28m 45s',
  findings: {
    high: 6,
    medium: 18,
    low: 28
  }
}, {
  id: 'SCAN-2026-007',
  name: 'Full Environment Scan — Vertex Digital',
  clientName: 'Vertex Digital',
  target: 'AWS (1 account)',
  status: 'Failed',
  started: 'Yesterday, 9:15 AM',
  duration: '4m 12s',
  findings: {
    high: 0,
    medium: 0,
    low: 0
  }
}, {
  id: 'SCAN-2026-008',
  name: 'Full Environment Scan — SecureStack',
  clientName: 'SecureStack',
  target: 'Azure + GCP (2 accounts)',
  status: 'Completed',
  started: '2 days ago',
  duration: '45m 30s',
  findings: {
    high: 3,
    medium: 14,
    low: 19
  }
}];
export function ScansPage() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedScan, setSelectedScan] = useState<Scan | null>(null);
  const filteredScans = scans.filter(scan => {
    const matchesFilter = activeFilter === 'All' || scan.status === activeFilter;
    const matchesSearch = !searchQuery || scan.name.toLowerCase().includes(searchQuery.toLowerCase()) || scan.clientName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });
  const totalFindings = scans.reduce((sum, s) => sum + s.findings.high + s.findings.medium + s.findings.low, 0);
  const totalHigh = scans.reduce((sum, s) => sum + s.findings.high, 0);
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Running':
        return <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-astro-green/10 text-astro-green border border-astro-green/20" data-id="element-1709">
            <span className="relative flex h-2 w-2" data-id="element-1710">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-astro-green opacity-75" data-id="element-1711"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-astro-green" data-id="element-1712"></span>
            </span>
            Running
          </span>;
      case 'Completed':
        return <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-astro-green/10 text-astro-green border border-astro-green/20" data-id="element-1713">
            <CheckCircle size={12} data-id="element-1714" />
            Completed
          </span>;
      case 'Failed':
        return <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-astro-danger/10 text-astro-danger border border-astro-danger/20" data-id="element-1715">
            <XCircle size={12} data-id="element-1716" />
            Failed
          </span>;
      default:
        return null;
    }
  };
  return <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-1717">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8" data-id="element-1718">
        <div data-id="element-1719">
          <h1 className="text-3xl font-bold text-white mb-2" data-id="element-1720">Scans</h1>
          <p className="text-astro-text-secondary text-lg" data-id="element-1721">
            Full environment scans across all managed clients
          </p>
        </div>
        <div className="flex items-center gap-4" data-id="element-1722">
          <div className="hidden md:flex items-center gap-4 text-sm" data-id="element-1723">
            <span className="text-astro-text-secondary" data-id="element-1724">
              <span className="font-mono font-bold text-white" data-id="element-1725">
                {totalFindings.toLocaleString()}
              </span>{' '}
              total findings
            </span>
            <span className="text-astro-text-secondary" data-id="element-1726">
              <span className="font-mono font-bold text-astro-danger" data-id="element-1727">
                {totalHigh}
              </span>{' '}
              critical
            </span>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-astro-green-dark hover:bg-astro-green text-white text-sm font-medium rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.15)]" data-id="element-1728">
            <Play size={16} data-id="element-1729" />
            New Scan
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 mb-6 justify-between items-center" data-id="element-1730">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto" data-id="element-1731">
          {['All', 'Running', 'Completed', 'Failed'].map(filter => <button key={filter} onClick={() => setActiveFilter(filter)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${activeFilter === filter ? 'bg-astro-green/15 text-astro-green border border-astro-green/30' : 'text-astro-text-secondary hover:text-white border border-transparent hover:border-astro-border'}`} data-id="element-1732">

              {filter}
              {filter !== 'All' && <span className="ml-1.5 text-xs opacity-60" data-id="element-1733">
                  ({scans.filter(s => s.status === filter).length})
                </span>}
            </button>)}
        </div>

        <div className="relative w-full md:w-64" data-id="element-1734">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-astro-text-secondary" size={16} data-id="element-1735" />

          <input type="text" placeholder="Search scans..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-astro-card border border-astro-border rounded-md pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1736" />

        </div>
      </div>

      {/* Scan List */}
      <div className="space-y-4" data-id="element-1737">
        {filteredScans.map(scan => {
        const totalScanFindings = scan.findings.high + scan.findings.medium + scan.findings.low;
        return <div key={scan.id} className="group bg-astro-card border border-astro-border rounded-lg p-5 hover:border-astro-green/30 transition-all" data-id="element-1738">

              <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between" data-id="element-1739">
                <div className="flex items-start gap-4" data-id="element-1740">
                  <div className="w-10 h-10 rounded-lg bg-astro-green/10 flex items-center justify-center shrink-0" data-id="element-1741">
                    <Shield className="text-astro-green" size={20} data-id="element-1742" />
                  </div>
                  <div data-id="element-1743">
                    <div className="flex items-center gap-3 mb-1 flex-wrap" data-id="element-1744">
                      <h3 className="text-lg font-bold text-white" data-id="element-1745">
                        {scan.name}
                      </h3>
                      {getStatusBadge(scan.status)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-astro-text-secondary flex-wrap" data-id="element-1746">
                      <span className="flex items-center gap-1.5" data-id="element-1747">
                        <Clock size={14} data-id="element-1748" /> Started {scan.started}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-astro-border hidden sm:block" data-id="element-1749"></span>
                      <span className="hidden sm:inline" data-id="element-1750">
                        Duration: {scan.duration}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-astro-border hidden sm:block" data-id="element-1751"></span>
                      <span className="hidden sm:inline font-mono text-xs opacity-70" data-id="element-1752">
                        {scan.id}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6 w-full lg:w-auto justify-between lg:justify-end" data-id="element-1753">
                  {scan.status === 'Completed' && <div className="flex items-center gap-3" data-id="element-1754">
                      <span className="text-sm font-mono font-bold text-white" data-id="element-1755">
                        {totalScanFindings.toLocaleString()}
                        <span className="text-astro-text-secondary font-normal ml-1" data-id="element-1756">
                          findings
                        </span>
                      </span>
                      {scan.findings.high > 0 && <span className="flex items-center gap-1.5 text-astro-danger text-sm font-bold bg-astro-danger/10 px-2 py-1 rounded border border-astro-danger/20" data-id="element-1757">
                          <AlertTriangle size={14} data-id="element-1758" />
                          {scan.findings.high} Critical
                        </span>}
                    </div>}

                  {scan.status === 'Failed' && <span className="text-xs text-astro-danger" data-id="element-1759">
                      Connection timeout — credentials may need refresh
                    </span>}

                  <button onClick={() => setSelectedScan(scan)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors shrink-0" data-id="element-1760">

                    View Results
                    <ChevronRight size={16} data-id="element-1761" />
                  </button>
                </div>
              </div>
            </div>;
      })}
      </div>

      <ScanResultsModal isOpen={selectedScan !== null} onClose={() => setSelectedScan(null)} scan={selectedScan} data-id="element-1762" />

    </div>;
}